import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, useMediaQuery } from '@mui/material';
import { toast } from 'react-hot-toast';
import EditProfileForm from './EditProfileForm';
import { myInfo, updateMyInfo } from '../../../api/endpoints';
import { ReactComponent as LoadingIcon } from '../../../assets/svg/loading.svg';
import axiosInstant from '../../../api';
import { defaultTabsPercentage, getProfilePercentage, ProFormTabs } from './tabs';
import { TabsPanel } from './tabsPanel';
import { MainContainer } from '../../../components/ContentWrapper';
import { useBoundStore } from '../../../store';
import { handleProfileImageUpdate, updateUserStore } from '../../profile/helper';

export default function EditMe() {
  const [searchParams] = useSearchParams();
  const prevValues = useRef('');
  const isSmallScreen = useMediaQuery('(max-width:1023px)');
  const [pro, setPro] = useState({ userid: 0 });

  const getTabFromParams = () => {
    const tab = searchParams.get('tab');
    return Object.values(ProFormTabs).includes(tab) ? tab : ProFormTabs.Basics;
  };
  const [selectedTab, updateTab] = useState(getTabFromParams);
  const [percentage, setPercentage] = useState(defaultTabsPercentage);
  const { updateUserInfo } = useBoundStore((state) => ({
    updateUserInfo: state.updateUserInfo,
  }));

  // React to query param changes (e.g. clicking banner while already on profile)
  useEffect(() => {
    const tab = getTabFromParams();
    if (tab !== selectedTab) {
      updateTab(tab);
    }
  }, [searchParams]);

  useEffect(() => {
    const getUser = async () => {
      try {
        const response = await axiosInstant.get(myInfo());
        const { data } = response;
        setPro(data);
      } catch (error) {
        // A failed load used to surface only as an unhandled rejection
        // (PostHog "Network Error" on /profile) while the spinner spun forever.
        toast.error('Could not load your profile. Please refresh the page.', { id: 'profile-load' });
      }
    };
    getUser();
  }, []);

  const updatePercentage = (profile) => {
    const res = getProfilePercentage(profile);
    const newValues = JSON.stringify(res);
    if (prevValues.current !== newValues) {
      prevValues.current = newValues;
      setPercentage({ ...res });
    }
  };

  const handleSubmit = async (values, actions) => {
    try {
      const avatarUrl = await handleProfileImageUpdate(values, pro, (errorMsg) => {
        toast.error(errorMsg);
        actions.setSubmitting(false);
      });
      
      if (avatarUrl === null && (values.avatarFile || (values.avatar && values.avatar.startsWith('data:image/')))) {
        return;
      }

      const response = await axiosInstant.patch(updateMyInfo(), values);
      const { data } = response;

      actions.setSubmitting(false);
      toast.success('Updated user information successfully!', { duration: 3000 });
      setPro(data);
      
      await updateUserStore(updateUserInfo);
      
      return data;
    } catch (error) {
      toast.error("Profile update failed");
      actions.setSubmitting(false);
    }
  };

  const tabProps = { selectedTab, updateTab };
  return (
    <MainContainer title="profile">
      <Box
        display="flex"
        gap={2}
        flexDirection={isSmallScreen ? 'column' : 'row'}
      >
        <TabsPanel
          {...tabProps}
          inline={isSmallScreen}
          percentage={{ ...percentage }}
        />
        <Box sx={{ backgroundColor: 'var(--surface)' }} width="100%">
          {!pro.userid && <LoadingIcon />}
          {pro.userid > 0 && (
            <EditProfileForm
              {...tabProps}
              pro={pro}
              onProUpdate={updatePercentage}
              handleSubmit={handleSubmit}
            />
          )}
        </Box>
      </Box>
    </MainContainer>
  );
}
